import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useState } from "react";
import ReactAnimateHeight from "react-animate-height";
import styles from "./FundraiserWidget.module.scss";
import type { SanityImageObject } from "@sanity/image-url";
import { FetchFundraiserResult } from "../../../../studio/sanity.types";
import { NavLink } from "../../../shared/components/Navbar/Navbar";
import { validateWidgetConfig } from "./validateWidgetConfig";
import { useFundraiserForm, FormData } from "./hooks/useFundraiserForm";
import { usePaneTransition } from "./hooks/usePaneTransition";
import { useRegisterDonation } from "./hooks/useRegisterDonation";
import { OrganizationInfoPane } from "./panes/OrganizationInfoPane";
import { DonationDetailsPane } from "./panes/DonationDetailsPane";
import { PaymentMethodPane } from "./panes/PaymentMethodPane";
import { BankTransferPane } from "./panes/BankTransferPane";
import { FormattingLocale } from "../../../../util/formatting";
import { supportsDonationFrequency } from "./paymentMethodAvailability";
import { RecurringDonation } from "../../../shared/components/Widget/types/Enums";
type WidgetConfig = NonNullable<NonNullable<FetchFundraiserResult["page"]>["fundraiser_widget_configuration"]>;
interface DonationWidgetProps {
  widgetConfig: WidgetConfig | null;
  suggestedAmounts?: number[] | null;
  monthlyDonations?: {
    enabled?: boolean;
    single_donation_text?: string;
    monthly_donation_text?: string;
  } | null;
  fundraiserId: string;
  organizationInfo: {
    organizationPageSlug: string;
    textTemplate: string;
    organization: NonNullable<FetchFundraiserResult["page"]>["fundraiser_organization"];
    databaseIds: {
      causeAreaId: number;
      organizationId: number;
    };
  };
  locale: FormattingLocale;
  onComplete?: (formData: FormData) => void;
}
export const FundraiserWidget: React.FC<DonationWidgetProps> = t0 => {
  const $ = _c(134);
  const {
    widgetConfig,
    suggestedAmounts,
    monthlyDonations,
    fundraiserId,
    organizationInfo,
    locale,
    onComplete: t1
  } = t0;
  t1 === undefined ? _temp : t1;
  let t2;
  if ($[0] !== widgetConfig?.payment_methods) {
    t2 = widgetConfig?.payment_methods?.map(_temp2) || [];
    $[0] = widgetConfig?.payment_methods;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    formData,
    updateField
  } = useFundraiserForm(t2, widgetConfig?.tax_deduction_enabled ? widgetConfig?.tax_deduction?.minimum_amount : undefined);
  const {
    step,
    paneRefs,
    currentHeight,
    getPaneClassName,
    isPaneVisible,
    goToNextStep,
    goToPreviousStep
  } = usePaneTransition(4);
  const [organizationInfoPaneRef, donationDetailsPaneRef, paymentMethodPaneRef, bankTransferPaneRef] = paneRefs;
  let t3;
  if ($[2] !== widgetConfig?.payment_methods) {
    t3 = widgetConfig?.payment_methods || [];
    $[2] = widgetConfig?.payment_methods;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== goToNextStep) {
    t4 = () => goToNextStep();
    $[4] = goToNextStep;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== fundraiserId || $[7] !== organizationInfo || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      fundraiserId,
      organizationInfo,
      paymentMethods: t3,
      onSuccess: t4
    };
    $[6] = fundraiserId;
    $[7] = organizationInfo;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    loading,
    kid,
    registerDonation
  } = useRegisterDonation(t5);
  const [privacyPolicyError, setPrivacyPolicyError] = useState(false);
  widgetConfig?.payment_methods;
  let t6;
  if ($[11] !== formData.recurring || $[12] !== widgetConfig?.payment_methods) {
    t6 = widgetConfig?.payment_methods?.filter(method_0 => supportsDonationFrequency(method_0._type, formData.recurring)) || [];
    $[11] = formData.recurring;
    $[12] = widgetConfig?.payment_methods;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const availablePaymentMethods = t6;
  let t7;
  let t8;
  if ($[14] !== availablePaymentMethods || $[15] !== formData.paymentMethod || $[16] !== updateField) {
    t7 = () => {
      if (availablePaymentMethods.length > 0 && !availablePaymentMethods.some(method_1 => method_1._type === formData.paymentMethod)) {
        updateField("paymentMethod", availablePaymentMethods[0]._type);
      }
    };
    t8 = [availablePaymentMethods, formData.paymentMethod, updateField];
    $[14] = availablePaymentMethods;
    $[15] = formData.paymentMethod;
    $[16] = updateField;
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  useEffect(t7, t8);
  let config;
  let t10;
  let t9;
  if ($[19] !== organizationInfo.organization || $[20] !== widgetConfig) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const validation = validateWidgetConfig(widgetConfig, organizationInfo.organization);
      if (!validation.valid) {
        t10 = validation.error;
        break bb0;
      }
      config = validation.config;
      t9 = config.payment_methods.some(_temp3);
    }
    $[19] = organizationInfo.organization;
    $[20] = widgetConfig;
    $[21] = config;
    $[22] = t10;
    $[23] = t9;
  } else {
    config = $[21];
    t10 = $[22];
    t9 = $[23];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  const hasMonthlyPaymentMethod = t9;
  let t11;
  if ($[24] !== monthlyDonations) {
    t11 = monthlyDonations?.enabled ? {
      single_donation_text: monthlyDonations.single_donation_text,
      monthly_donation_text: monthlyDonations.monthly_donation_text
    } : undefined;
    $[24] = monthlyDonations;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const monthlyDonationConfig = t11;
  if (monthlyDonationConfig && !hasMonthlyPaymentMethod) {
    return "No configured payment method supports monthly donations in the fundraiser widget";
  }
  if (monthlyDonationConfig && (!monthlyDonationConfig.single_donation_text || !monthlyDonationConfig.monthly_donation_text)) {
    return "Missing monthly donation labels for fundraiser widget";
  }
  if (availablePaymentMethods.length === 0) {
    return "No payment method supports the selected donation frequency";
  }
  let t12;
  if ($[26] !== suggestedAmounts) {
    t12 = Array.isArray(suggestedAmounts) ? suggestedAmounts.filter(_temp4) : [];
    $[26] = suggestedAmounts;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const normalizedSuggestedAmounts = t12;
  let t13;
  if ($[28] !== config.privacy_policy.require_checkbox || $[29] !== formData || $[30] !== registerDonation) {
    t13 = () => {
      if (config.privacy_policy?.require_checkbox && !formData.privacyPolicyAccepted) {
        setPrivacyPolicyError(true);
        return;
      }
      setPrivacyPolicyError(false);
      registerDonation(formData);
    };
    $[28] = config.privacy_policy.require_checkbox;
    $[29] = formData;
    $[30] = registerDonation;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const handlePaymentMethodSubmit = t13;
  const t14 = `translateX(${step === 0 ? "-0.5rem" : "0"})`;
  const t15 = step === 0 ? 0 : 1;
  let t16;
  if ($[32] !== t14 || $[33] !== t15) {
    t16 = {
      transform: t14,
      opacity: t15
    };
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== goToPreviousStep || $[36] !== t16) {
    t17 = <button className={styles["donation-widget__back-button"]} onClick={goToPreviousStep} aria-label="Go back" data-cy="fundraiser-back-button" style={t16}>←</button>;
    $[35] = goToPreviousStep;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const t18 = typeof currentHeight === "number" ? currentHeight : "auto";
  let t19;
  if ($[38] !== getPaneClassName) {
    t19 = getPaneClassName(0);
    $[38] = getPaneClassName;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== isPaneVisible) {
    t20 = isPaneVisible(0);
    $[40] = isPaneVisible;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const t21 = organizationInfo.organization.logo as SanityImageObject;
  let t22;
  if ($[42] !== organizationInfo.organization.name || $[43] !== organizationInfo.organizationPageSlug || $[44] !== organizationInfo.textTemplate || $[45] !== t21) {
    t22 = {
      name: organizationInfo.organization.name,
      logo: t21,
      textTemplate: organizationInfo.textTemplate,
      organizationSlug: organizationInfo.organizationPageSlug
    };
    $[42] = organizationInfo.organization.name;
    $[43] = organizationInfo.organizationPageSlug;
    $[44] = organizationInfo.textTemplate;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== config.header || $[48] !== goToNextStep || $[49] !== organizationInfoPaneRef || $[50] !== t19 || $[51] !== t20 || $[52] !== t22) {
    t23 = <OrganizationInfoPane ref={organizationInfoPaneRef} className={t19} visible={t20} organizationInfo={t22} buttonText={config.header} onNext={goToNextStep} />;
    $[47] = config.header;
    $[48] = goToNextStep;
    $[49] = organizationInfoPaneRef;
    $[50] = t19;
    $[51] = t20;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== getPaneClassName) {
    t24 = getPaneClassName(1);
    $[54] = getPaneClassName;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== isPaneVisible) {
    t25 = isPaneVisible(1);
    $[56] = isPaneVisible;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  const t26 = monthlyDonationConfig as {
    single_donation_text: string;
    monthly_donation_text: string;
  } | undefined;
  let t27;
  if ($[58] !== config.currency_symbol || $[59] !== config.donation_amount_label || $[60] !== config.message_label || $[61] !== config.name_label || $[62] !== config.next_button_text || $[63] !== config.show_name_label || $[64] !== normalizedSuggestedAmounts || $[65] !== t26) {
    t27 = {
      suggested_amounts: normalizedSuggestedAmounts,
      currency_symbol: config.currency_symbol,
      donation_amount_label: config.donation_amount_label,
      name_label: config.name_label,
      message_label: config.message_label,
      show_name_label: config.show_name_label,
      next_button_text: config.next_button_text,
      monthly_donations: t26
    };
    $[58] = config.currency_symbol;
    $[59] = config.donation_amount_label;
    $[60] = config.message_label;
    $[61] = config.name_label;
    $[62] = config.next_button_text;
    $[63] = config.show_name_label;
    $[64] = normalizedSuggestedAmounts;
    $[65] = t26;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== donationDetailsPaneRef || $[68] !== formData || $[69] !== goToNextStep || $[70] !== locale || $[71] !== t24 || $[72] !== t25 || $[73] !== t27 || $[74] !== updateField) {
    t28 = <DonationDetailsPane ref={donationDetailsPaneRef} className={t24} visible={t25} formData={formData} onChange={updateField} onSubmit={goToNextStep} config={t27} locale={locale} />;
    $[67] = donationDetailsPaneRef;
    $[68] = formData;
    $[69] = goToNextStep;
    $[70] = locale;
    $[71] = t24;
    $[72] = t25;
    $[73] = t27;
    $[74] = updateField;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  let t29;
  if ($[76] !== getPaneClassName) {
    t29 = getPaneClassName(2);
    $[76] = getPaneClassName;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== isPaneVisible) {
    t30 = isPaneVisible(2);
    $[78] = isPaneVisible;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== config.tax_deduction || $[81] !== config.tax_deduction_enabled) {
    t31 = config.tax_deduction_enabled && config.tax_deduction ? {
      label: config.tax_deduction.label,
      tooltip_text: config.tax_deduction.tooltip_text,
      ssn_label: config.tax_deduction.ssn_label,
      ssn_invalid_error_text: config.tax_deduction.ssn_invalid_error_text,
      ssn_suspicious_error_text: config.tax_deduction.ssn_suspicious_error_text
    } : undefined;
    $[80] = config.tax_deduction;
    $[81] = config.tax_deduction_enabled;
    $[82] = t31;
  } else {
    t31 = $[82];
  }
  let t32;
  if ($[83] !== config.newsletter || $[84] !== config.newsletter_enabled) {
    t32 = config.newsletter_enabled && config.newsletter ? {
      label: config.newsletter.label
    } : undefined;
    $[83] = config.newsletter;
    $[84] = config.newsletter_enabled;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  const t33 = config.privacy_policy.privacy_policy_url as NavLink;
  let t34;
  if ($[86] !== config.privacy_policy.require_checkbox || $[87] !== config.privacy_policy.required_error_text || $[88] !== config.privacy_policy.text || $[89] !== t33) {
    t34 = {
      text: config.privacy_policy.text,
      require_checkbox: config.privacy_policy.require_checkbox,
      required_error_text: config.privacy_policy.required_error_text,
      privacy_policy_url: t33
    };
    $[86] = config.privacy_policy.require_checkbox;
    $[87] = config.privacy_policy.required_error_text;
    $[88] = config.privacy_policy.text;
    $[89] = t33;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  let t35;
  if ($[91] !== availablePaymentMethods || $[92] !== config.allow_anonymous_donations || $[93] !== config.email_label || $[94] !== t31 || $[95] !== t32 || $[96] !== t34) {
    t35 = {
      tax_deduction: t31,
      newsletter: t32,
      email_label: config.email_label,
      privacy_policy: t34,
      payment_methods: availablePaymentMethods,
      allow_anonymous_donations: config.allow_anonymous_donations
    };
    $[91] = availablePaymentMethods;
    $[92] = config.allow_anonymous_donations;
    $[93] = config.email_label;
    $[94] = t31;
    $[95] = t32;
    $[96] = t34;
    $[97] = t35;
  } else {
    t35 = $[97];
  }
  let t36;
  if ($[98] !== formData || $[99] !== handlePaymentMethodSubmit || $[100] !== loading || $[101] !== locale || $[102] !== paymentMethodPaneRef || $[103] !== privacyPolicyError || $[104] !== t29 || $[105] !== t30 || $[106] !== t35 || $[107] !== updateField) {
    t36 = <PaymentMethodPane ref={paymentMethodPaneRef} className={t29} visible={t30} formData={formData} onChange={updateField} onSubmit={handlePaymentMethodSubmit} loading={loading} config={t35} privacyPolicyError={privacyPolicyError} locale={locale} />;
    $[98] = formData;
    $[99] = handlePaymentMethodSubmit;
    $[100] = loading;
    $[101] = locale;
    $[102] = paymentMethodPaneRef;
    $[103] = privacyPolicyError;
    $[104] = t29;
    $[105] = t30;
    $[106] = t35;
    $[107] = updateField;
    $[108] = t36;
  } else {
    t36 = $[108];
  }
  let t37;
  if ($[109] !== getPaneClassName) {
    t37 = getPaneClassName(3);
    $[109] = getPaneClassName;
    $[110] = t37;
  } else {
    t37 = $[110];
  }
  let t38;
  if ($[111] !== isPaneVisible) {
    t38 = isPaneVisible(3);
    $[111] = isPaneVisible;
    $[112] = t38;
  } else {
    t38 = $[112];
  }
  const t39 = formData.paymentMethod === "bank" || formData.paymentMethod === "dkbank";
  let t40;
  if ($[113] !== config.bank_account_details) {
    t40 = config.bank_account_details ? {
      account_number_prefix: config.bank_account_details.account_number_prefix,
      account_number: config.bank_account_details.account_number,
      kid_prefix: config.bank_account_details.kid_prefix,
      transfer_delay_text: config.bank_account_details.transfer_delay_text,
      account_owner_text: config.bank_account_details.account_owner_text,
      bank_transfer_info: config.bank_account_details.bank_transfer_info
    } : undefined;
    $[113] = config.bank_account_details;
    $[114] = t40;
  } else {
    t40 = $[114];
  }
  let t41;
  if ($[115] !== bankTransferPaneRef || $[116] !== kid || $[117] !== t37 || $[118] !== t38 || $[119] !== t39 || $[120] !== t40) {
    t41 = <BankTransferPane ref={bankTransferPaneRef} className={t37} visible={t38} kid={kid} showBankInfo={t39} bankDetails={t40} />;
    $[115] = bankTransferPaneRef;
    $[116] = kid;
    $[117] = t37;
    $[118] = t38;
    $[119] = t39;
    $[120] = t40;
    $[121] = t41;
  } else {
    t41 = $[121];
  }
  let t42;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <div className={styles["donation-widget__bottom_shade"]} />;
    $[122] = t42;
  } else {
    t42 = $[122];
  }
  let t43;
  if ($[123] !== t23 || $[124] !== t28 || $[125] !== t36 || $[126] !== t41) {
    t43 = <div className={styles["donation-widget__inner"]}>{t23}{t28}{t36}{t41}{t42}</div>;
    $[123] = t23;
    $[124] = t28;
    $[125] = t36;
    $[126] = t41;
    $[127] = t43;
  } else {
    t43 = $[127];
  }
  let t44;
  if ($[128] !== t18 || $[129] !== t43) {
    t44 = <ReactAnimateHeight duration={300} height={t18} className={styles["donation-widget__panes"]}>{t43}</ReactAnimateHeight>;
    $[128] = t18;
    $[129] = t43;
    $[130] = t44;
  } else {
    t44 = $[130];
  }
  let t45;
  if ($[131] !== t17 || $[132] !== t44) {
    t45 = <div className={styles["donation-widget"]} data-cy="fundraiser-widget">{t17}{t44}</div>;
    $[131] = t17;
    $[132] = t44;
    $[133] = t45;
  } else {
    t45 = $[133];
  }
  return t45;
};
function _temp() {}
function _temp2(method) {
  return method._type;
}
function _temp3(method_2) {
  return supportsDonationFrequency(method_2._type, RecurringDonation.RECURRING);
}
function _temp4(amount) {
  return typeof amount === "number" && amount > 0;
}